import React, {useMemo} from 'react';

import prop from "ramda/src/prop";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";

import Grid from "@mui/material/Grid";

import Api from "../../services/Api";
import { ModalProvider } from "../../contexts/ModalContext";
import useAuth from "../../routes/login/AuthContext";
import { AddToOtherSubnetworksButton } from "../../components/GenderSettings/AddToOtherSubnetworksButton";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useNetworkContext } from "../../contexts/NetworkContext/context";
import { useNetwork } from "../../hooks/useNetwork";
import { L2Selector } from "../../common/network/NetworkSelectors";
import { AddToOtherNetworksButton } from "../../components/GenderSettings/AddToOtherNetworksButton";
import { ProfilePersonalPronouns } from "../../components/GenderSettings/ProfilePersonalPronouns";
import { GenderEnabled } from "../../components/GenderSettings/GenderEnabled";
import { GenderSectionTitle } from "../../components/GenderSettings/GenderSectionTitle";
import { GenderSettingsView } from "../../components/GenderSettings/GenderSettingsView";
import { GenderSectionExplaner } from "../../components/GenderSettings/GenderSectionExplaner";
import { GenderOptionsTable } from "../../components/GenderSettings/GenderOptionsTable";
import { AddGenderOptionModal } from "../../components/GenderSettings/AddGenderOptionModal";
import { GenderGroupsTable } from "../../components/GenderSettings/GenderGroupsTable";
import { AddMatchingOptionModal } from "../../components/GenderSettings/AddMatchingOptionModal";

export const Gender = () => {
  const { getAuthToken, isSuperAdmin } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [subnetwork, setSubnetwork] = React.useState<number>(0);
  const { updateGenderNetworkSettingsValues } = useNetworkContext();
  const { getL2, selectedNetwork } = useNetwork();

  const l2 = React.useMemo(() => getL2(selectedNetwork.l1Id, subnetwork), [
    selectedNetwork.l1Id,
    subnetwork,
    getL2,
  ]);

  const onComplete = () => {
    showSuccessSnackbar("Saved Changes");
  };

  const handleError = () => {
    showErrorSnackbar("An Error Occurred");
  };

  const updateGenderNetworkSettings = React.useCallback(
    (data: Record<string, boolean | string>) => () => {
      updateGenderNetworkSettingsValues(selectedNetwork.l1Id, subnetwork, data);
    },
    [updateGenderNetworkSettingsValues, selectedNetwork.l1Id, subnetwork]
  );

  const handleSuccess = (data: Record<string, boolean | string>) =>
    ifElse(
      prop("success"),
      compose(onComplete, updateGenderNetworkSettings(data)),
      handleError
    );

  const handleChange = async (key: string, value: boolean | string) => {
    return Api.setMathingRules(
      selectedNetwork.l1Id,
      {
        subnetworkCommands: [
          {
            l1Id: selectedNetwork.l1Id,
            l2Id: subnetwork,
            [key]: value,
          },
        ],
        unitTypeCommands: [],
      },
      await getAuthToken()
    )
      .then(handleSuccess({ [key]: value }))
      .catch(handleError);
  };

  const isReadOnly = useMemo(() =>  (l2?.matchingRoundStatus === "ARCHIVED" || l2?.matchingRoundStatus === "CLOSED") , [l2]);

  return (
      <Grid container>
        <Grid container xs={12} mb={6}>
          <Grid container xs={6}>
            <L2Selector onSelectHandler={setSubnetwork} />
          </Grid>
          <Grid container spacing={1} xs={6} alignItems="center" justifyContent="flex-end">
            <Grid item>
              <ModalProvider>
                <AddToOtherSubnetworksButton l2Id={subnetwork} />
              </ModalProvider>
            </Grid>
            {isSuperAdmin() && (
              <Grid item>
                <ModalProvider>
                  <AddToOtherNetworksButton l2Id={subnetwork}/>
                </ModalProvider>
              </Grid>
            )}
          </Grid>
        </Grid>
        {!!subnetwork && (
          <>
            <Grid item container spacing={3}>
              <Grid item xs={12}>
                <ProfilePersonalPronouns l2={l2} isDisabled={isReadOnly}/>
              </Grid>
              <Grid item xs={12}>
                <GenderEnabled l2={l2} onChange={handleChange} isDisabled={isReadOnly}/>
              </Grid>
              {!!l2?.genderNetworkSettingsTransport.genderSelectionEnabled && (
                <>
                  <Grid item xs={12}>
                    <GenderSectionTitle 
                      l2={l2} 
                      updateHandler={handleChange}
                      isDisabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <GenderSectionExplaner
                      l2={l2}
                      updateHandler={handleChange}
                      isDisabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <GenderSettingsView 
                      l2={l2} 
                      onChange={handleChange}
                      isDisabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ModalProvider>
                      <GenderOptionsTable 
                        subnetwork={subnetwork}
                        isDisabled={isReadOnly}
                      />
                      <AddGenderOptionModal subnetwork={subnetwork} />
                    </ModalProvider>
                  </Grid>
                  <Grid item xs={12}>
                    <ModalProvider>
                      <GenderGroupsTable 
                        subnetwork={subnetwork}
                        isDisabled={isReadOnly}
                      />
                      <AddMatchingOptionModal subnetwork={subnetwork} />
                    </ModalProvider>
                  </Grid>
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
  );
};
